/**
 * Termómetro para Panadería - Masa Madre Monterrey
 * JavaScript Vanilla para Shopify - Con Geolocalización Dinámica
 * Instalación: Copiar a assets/thermometer.js
 */

(function() {
  'use strict';

  // ===== CONFIGURACIÓN =====
  const CONFIG = {
    // Coordenadas por defecto (Monterrey, México) - Se usan si no hay geolocalización
    DEFAULT_LAT: 25.6866,
    DEFAULT_LON: -100.3161,
    DEFAULT_ALTITUDE: 537,
    DEFAULT_CITY: 'Monterrey',
    DEFAULT_COUNTRY: 'México',
    // APIs
    WEATHER_API_URL: 'https://api.open-meteo.com/v1/forecast',
    GEOCODING_API_URL: 'https://nominatim.openstreetmap.org/reverse',
    // Rango óptimo de temperatura para fermentación
    OPTIMAL_TEMP_MIN: 20,
    OPTIMAL_TEMP_MAX: 28
  };

  // ===== ESTADO =====
  let isLoading = false;
  let userLocation = null;

  // ===== ELEMENTOS DEL DOM =====
  const elements = {};

  // ===== INICIALIZACIÓN =====
  function init() {
    // Cachear elementos del DOM
    cacheElements();
    
    // Configurar event listeners
    setupEventListeners();
    
    // Intentar obtener ubicación del usuario
    getUserLocation();
  }

  function cacheElements() {
    elements.cityState = document.getElementById('mm-city-state');
    elements.currentDate = document.getElementById('mm-current-date');
    elements.lastUpdated = document.getElementById('mm-last-updated');
    elements.weatherIcon = document.getElementById('mm-weather-icon');
    elements.temperature = document.getElementById('mm-temperature');
    elements.weatherDescription = document.getElementById('mm-weather-description');
    elements.humidity = document.getElementById('mm-humidity');
    elements.altitude = document.getElementById('mm-altitude');
    elements.forecastMin = document.getElementById('mm-forecast-min');
    elements.forecastMax = document.getElementById('mm-forecast-max');
    elements.thermometerScale = document.getElementById('mm-thermometer-scale');
    elements.optimalRange = document.getElementById('mm-optimal-range');
    elements.temperatureIndicator = document.getElementById('mm-temperature-indicator');
    elements.indicatorValue = document.getElementById('mm-indicator-value');
    elements.errorMessage = document.getElementById('mm-error-message');
    elements.fermentationLevel = document.getElementById('mm-fermentation-level');
    elements.levelValue = document.getElementById('mm-level-value');
    elements.levelDescription = document.getElementById('mm-level-description');
    elements.recMasaMadre = document.getElementById('mm-rec-masa-madre');
    elements.recAgua = document.getElementById('mm-rec-agua');
    elements.recFermentacion = document.getElementById('mm-rec-fermentacion');
    elements.recRefrigeracion = document.getElementById('mm-rec-refrigeracion');
    elements.altitudeWarning = document.getElementById('mm-altitude-warning');
    elements.humidityWarning = document.getElementById('mm-humidity-warning');
    elements.proTip = document.getElementById('mm-pro-tip');
    elements.refreshButton = document.getElementById('mm-refresh-button');
    elements.refreshIcon = document.getElementById('mm-refresh-icon');
    elements.refreshText = document.getElementById('mm-refresh-text');
  }

  function setupEventListeners() {
    if (elements.refreshButton) {
      elements.refreshButton.addEventListener('click', handleRefresh);
    }
  }

  // ===== GEOLOCALIZACIÓN =====
  function getUserLocation() {
    // Mostrar mensaje de buscando ubicación
    if (elements.cityState) {
      elements.cityState.textContent = 'Detectando tu ubicación...';
    }

    // Verificar si el navegador soporta geolocalización
    if (!navigator.geolocation) {
      console.warn('Geolocalización no soportada, usando ubicación por defecto');
      useDefaultLocation();
      return;
    }

    // Intentar obtener ubicación del usuario
    navigator.geolocation.getCurrentPosition(
      // Éxito
      function(position) {
        userLocation = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          altitude: position.coords.altitude // Puede ser null
        };
        console.log('Ubicación obtenida:', userLocation);
        fetchWeatherData(userLocation.lat, userLocation.lon);
      },
      // Error
      function(error) {
        console.warn('Error de geolocalización:', error.message);
        let message = 'No se pudo obtener tu ubicación';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permiso de ubicación denegado. Usando Monterrey como ubicación.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Información de ubicación no disponible.';
            break;
          case error.TIMEOUT:
            message = 'Tiempo de espera agotado para obtener ubicación.';
            break;
        }
        
        // Mostrar mensaje temporal
        if (elements.cityState) {
          elements.cityState.textContent = message;
        }
        
        // Usar ubicación por defecto después de un momento
        setTimeout(function() {
          useDefaultLocation();
        }, 2000);
      },
      // Opciones
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache por 5 minutos
      }
    );
  }

  function useDefaultLocation() {
    userLocation = {
      lat: CONFIG.DEFAULT_LAT,
      lon: CONFIG.DEFAULT_LON,
      altitude: CONFIG.DEFAULT_ALTITUDE,
      city: CONFIG.DEFAULT_CITY,
      country: CONFIG.DEFAULT_COUNTRY,
      isDefault: true
    };
    fetchWeatherData(userLocation.lat, userLocation.lon);
  }

  // ===== OBTENER NOMBRE DE CIUDAD =====
  async function getCityName(lat, lon) {
    try {
      const url = `${CONFIG.GEOCODING_API_URL}?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`;
      
      const response = await fetch(url, {
        headers: {
          'Accept-Language': 'es'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error en geocoding');
      }
      
      const data = await response.json();
      
      // Extraer nombre de ciudad/pueblo
      const address = data.address || {};
      const city = address.city || address.town || address.village || address.municipality || address.county || 'Ubicación desconocida';
      const country = address.country || '';
      
      return { city, country };
      
    } catch (error) {
      console.warn('Error al obtener nombre de ciudad:', error);
      return { city: 'Tu ubicación', country: '' };
    }
  }

  // ===== MANEJO DE DATOS DEL CLIMA =====
  async function fetchWeatherData(lat, lon) {
    if (isLoading) return;
    
    setLoading(true);
    hideError();

    try {
      // Obtener nombre de ciudad y datos del clima en paralelo
      const [geoData, weatherResponse] = await Promise.all([
        userLocation && !userLocation.isDefault ? getCityName(lat, lon) : Promise.resolve({ 
          city: CONFIG.DEFAULT_CITY, 
          country: CONFIG.DEFAULT_COUNTRY 
        }),
        fetch(`${CONFIG.WEATHER_API_URL}?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=1`)
      ]);
      
      if (!weatherResponse.ok) {
        throw new Error(`Error HTTP: ${weatherResponse.status}`);
      }
      
      const data = await weatherResponse.json();
      
      // Procesar y mostrar datos
      const weatherData = {
        temperature: Math.round(data.current.temperature_2m),
        humidity: data.current.relative_humidity_2m,
        tempMin: Math.round(data.daily.temperature_2m_min[0]),
        tempMax: Math.round(data.daily.temperature_2m_max[0]),
        weatherCode: data.current.weather_code,
        altitude: Math.round(data.elevation) || userLocation?.altitude || CONFIG.DEFAULT_ALTITUDE,
        city: geoData.city,
        country: geoData.country,
        timezone: data.timezone
      };
      
      displayWeatherData(weatherData);
      
    } catch (error) {
      console.error('Error al obtener datos del clima:', error);
      showError('No se pudieron obtener los datos del clima. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  }

  function displayWeatherData(data) {
    // Actualizar ubicación y fecha
    if (elements.cityState) {
      elements.cityState.textContent = `${data.city}${data.country ? ', ' + data.country : ''}`;
    }
    
    if (elements.currentDate) {
      elements.currentDate.textContent = formatDate(new Date());
    }
    
    if (elements.lastUpdated) {
      elements.lastUpdated.textContent = `Última actualización: ${formatTime(new Date())}`;
    }

    // Actualizar datos del clima
    const weatherIcon = getWeatherIcon(data.weatherCode);
    
    if (elements.weatherIcon) {
      elements.weatherIcon.innerHTML = `<span class="mm-weather-emoji">${weatherIcon.icon}</span>`;
    }
    
    if (elements.temperature) {
      elements.temperature.textContent = `${data.temperature}°C`;
    }
    
    if (elements.weatherDescription) {
      elements.weatherDescription.textContent = weatherIcon.description;
    }
    
    if (elements.humidity) {
      elements.humidity.textContent = data.humidity;
    }
    
    if (elements.altitude) {
      elements.altitude.textContent = data.altitude;
    }
    
    if (elements.forecastMin) {
      elements.forecastMin.textContent = data.tempMin;
    }
    
    if (elements.forecastMax) {
      elements.forecastMax.textContent = data.tempMax;
    }

    // Actualizar termómetro visual
    updateThermometerVisual(data.temperature, data.tempMin, data.tempMax);

    // Calcular y mostrar recomendaciones
    const recommendations = calculateRecommendations(data.temperature, data.humidity, data.altitude);
    displayRecommendations(recommendations);
  }

  // ===== TERMÓMETRO VISUAL =====
  function updateThermometerVisual(temperature, tempMin, tempMax) {
    const minRange = Math.min(tempMin - 5, 0);
    const maxRange = Math.max(tempMax + 5, 40);
    const range = maxRange - minRange;

    // Actualizar rango óptimo (20-28°C)
    const optimalStart = ((CONFIG.OPTIMAL_TEMP_MIN - minRange) / range) * 100;
    const optimalWidth = ((CONFIG.OPTIMAL_TEMP_MAX - CONFIG.OPTIMAL_TEMP_MIN) / range) * 100;
    
    if (elements.optimalRange) {
      elements.optimalRange.style.bottom = `${optimalStart}%`;
      elements.optimalRange.style.height = `${optimalWidth}%`;
    }

    // Calcular posición del indicador de temperatura
    const clampedTemp = Math.max(minRange, Math.min(maxRange, temperature));
    const tempPosition = ((clampedTemp - minRange) / range) * 100;
    
    if (elements.temperatureIndicator) {
      elements.temperatureIndicator.style.bottom = `${tempPosition}%`;
      elements.temperatureIndicator.style.display = 'flex';
      elements.temperatureIndicator.style.visibility = 'visible';
      elements.temperatureIndicator.style.opacity = '1';
    }
    
    if (elements.indicatorValue) {
      elements.indicatorValue.textContent = `${temperature}°C`;
    }

    if (elements.temperature) {
      elements.temperature.textContent = `${temperature}°C`;
    }
  }

  // ===== RECOMENDACIONES =====
  function calculateRecommendations(temp, humidity, altitude) {
    let level, levelClass, description, masaMadrePercent, waterTemp, fermentationTime, refrigerationHours, proTip;
    let altitudeWarning = '';
    let humidityWarning = '';

    if (temp < 15) {
      level = 'Alto';
      levelClass = 'mm-level-hard';
      description = 'Clima frío: La fermentación será lenta. Se recomienda mayor cantidad de masa madre.';
      masaMadrePercent = '25-30%';
      waterTemp = '30-35°C';
      fermentationTime = '8-12 horas';
      refrigerationHours = '12-18h';
      proTip = 'En climas fríos, mantén tu masa en un lugar cálido (cerca del horno o usa un probador). Considera usar agua tibia para activar mejor la masa madre.';
    } else if (temp >= 15 && temp < 20) {
      level = 'Moderado';
      levelClass = 'mm-level-medium';
      description = 'Temperatura fresca: Condiciones favorables para desarrollo de sabor.';
      masaMadrePercent = '20-25%';
      waterTemp = '26-30°C';
      fermentationTime = '6-8 horas';
      refrigerationHours = '12-24h';
      proTip = 'Temperatura ideal para desarrollar sabores complejos. Considera hacer fermentaciones largas en refrigeración para mejorar el perfil organoléptico.';
    } else if (temp >= 20 && temp < 28) {
      level = 'Óptimo';
      levelClass = 'mm-level-optimal';
      description = 'Condiciones perfectas para la fermentación de masa madre.';
      masaMadrePercent = '15-20%';
      waterTemp = '22-26°C';
      fermentationTime = '4-6 horas';
      refrigerationHours = '8-12h';
      proTip = 'Condiciones ideales para pan artesanal. Aprovecha para experimentar con fermentaciones mixtas (parte ambiente, parte refrigeración).';
    } else if (temp >= 28 && temp < 35) {
      level = 'Moderado';
      levelClass = 'mm-level-medium';
      description = 'Clima cálido: La fermentación será rápida. Monitorea con frecuencia.';
      masaMadrePercent = '10-15%';
      waterTemp = '18-22°C';
      fermentationTime = '3-4 horas';
      refrigerationHours = '4-8h';
      proTip = 'En clima cálido, usa agua fría y reduce el porcentaje de masa madre. La fermentación puede acelerarse, así que vigila la masa cada 30 minutos.';
    } else {
      level = 'Alto';
      levelClass = 'mm-level-hard';
      description = 'Clima muy caluroso: Riesgo de sobre-fermentación. Extrema precauciones.';
      masaMadrePercent = '5-10%';
      waterTemp = '15-18°C';
      fermentationTime = '2-3 horas';
      refrigerationHours = '2-4h';
      proTip = '¡Cuidado! En calor extremo, trabaja rápido y mantén todo frío. Considera fermentar completamente en refrigeración para evitar sobre-fermentación.';
    }

    // Advertencia de altitud
    if (altitude > 1500) {
      altitudeWarning = '⚠️ A gran altitud (' + altitude + 'm), la fermentación es más rápida debido a la menor presión atmosférica. Reduce los tiempos de fermentación un 10-15%.';
    } else if (altitude > 1000) {
      altitudeWarning = 'ℹ️ A esta altitud (' + altitude + 'm), la fermentación puede ser ligeramente más rápida. Considera reducir los tiempos un 5-10%.';
    }

    // Advertencia de humedad
    if (humidity < 30) {
      humidityWarning = '⚠️ Humedad muy baja: La masa tenderá a secarse. Cubre con plástico o usa un recipiente hermético. Considera aumentar la hidratación un 2-5%.';
    } else if (humidity < 45) {
      humidityWarning = 'ℹ️ Humedad baja: Cubre tu masa durante la fermentación para evitar que se forme costra.';
    } else if (humidity > 80) {
      humidityWarning = '⚠️ Humedad muy alta: La masa puede volverse pegajosa. Considera reducir la hidratación un 2-3% y usa más harina para formar.';
    }

    return {
      level,
      levelClass,
      description,
      masaMadrePercent,
      waterTemp,
      fermentationTime,
      refrigerationHours,
      proTip,
      altitudeWarning,
      humidityWarning
    };
  }

  function displayRecommendations(rec) {
    if (elements.fermentationLevel) {
      elements.fermentationLevel.className = `mm-fermentation-level ${rec.levelClass}`;
    }
    
    if (elements.levelValue) {
      elements.levelValue.textContent = rec.level;
    }
    
    if (elements.levelDescription) {
      elements.levelDescription.textContent = rec.description;
    }

    if (elements.recMasaMadre) {
      elements.recMasaMadre.textContent = rec.masaMadrePercent;
    }
    
    if (elements.recAgua) {
      elements.recAgua.textContent = rec.waterTemp;
    }
    
    if (elements.recFermentacion) {
      elements.recFermentacion.textContent = rec.fermentationTime;
    }
    
    if (elements.recRefrigeracion) {
      elements.recRefrigeracion.textContent = rec.refrigerationHours;
    }

    if (elements.altitudeWarning) {
      if (rec.altitudeWarning) {
        elements.altitudeWarning.textContent = rec.altitudeWarning;
        elements.altitudeWarning.style.display = 'block';
      } else {
        elements.altitudeWarning.style.display = 'none';
      }
    }

    if (elements.humidityWarning) {
      if (rec.humidityWarning) {
        elements.humidityWarning.textContent = rec.humidityWarning;
        elements.humidityWarning.style.display = 'block';
      } else {
        elements.humidityWarning.style.display = 'none';
      }
    }

    if (elements.proTip) {
      elements.proTip.textContent = rec.proTip;
    }
  }

  // ===== UTILIDADES =====
  function getWeatherIcon(code) {
    const weatherCodes = {
      0: { icon: '☀️', description: 'Despejado' },
      1: { icon: '🌤️', description: 'Mayormente despejado' },
      2: { icon: '⛅', description: 'Parcialmente nublado' },
      3: { icon: '☁️', description: 'Nublado' },
      45: { icon: '🌫️', description: 'Neblina' },
      48: { icon: '🌫️', description: 'Neblina con escarcha' },
      51: { icon: '🌧️', description: 'Llovizna ligera' },
      53: { icon: '🌧️', description: 'Llovizna moderada' },
      55: { icon: '🌧️', description: 'Llovizna densa' },
      61: { icon: '🌧️', description: 'Lluvia ligera' },
      63: { icon: '🌧️', description: 'Lluvia moderada' },
      65: { icon: '🌧️', description: 'Lluvia fuerte' },
      71: { icon: '🌨️', description: 'Nevada ligera' },
      73: { icon: '🌨️', description: 'Nevada moderada' },
      75: { icon: '🌨️', description: 'Nevada fuerte' },
      77: { icon: '🌨️', description: 'Granos de nieve' },
      80: { icon: '🌦️', description: 'Chubascos ligeros' },
      81: { icon: '🌦️', description: 'Chubascos moderados' },
      82: { icon: '🌦️', description: 'Chubascos violentos' },
      85: { icon: '🌨️', description: 'Chubascos de nieve ligeros' },
      86: { icon: '🌨️', description: 'Chubascos de nieve fuertes' },
      95: { icon: '⛈️', description: 'Tormenta' },
      96: { icon: '⛈️', description: 'Tormenta con granizo ligero' },
      99: { icon: '⛈️', description: 'Tormenta con granizo fuerte' }
    };
    
    return weatherCodes[code] || { icon: '🌡️', description: 'Desconocido' };
  }

  function formatDate(date) {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-MX', options);
  }

  function formatTime(date) {
    const options = { 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return date.toLocaleTimeString('es-MX', options);
  }

  // ===== ESTADOS DE UI =====
  function setLoading(loading) {
    isLoading = loading;
    
    if (elements.refreshButton) {
      elements.refreshButton.disabled = loading;
    }
    
    if (elements.refreshIcon) {
      if (loading) {
        elements.refreshIcon.classList.add('mm-spinning');
      } else {
        elements.refreshIcon.classList.remove('mm-spinning');
      }
    }
    
    if (elements.refreshText) {
      elements.refreshText.textContent = loading ? ' Actualizando...' : ' Actualizar Datos';
    }
  }

  function showError(message) {
    if (elements.errorMessage) {
      elements.errorMessage.textContent = message;
      elements.errorMessage.style.display = 'block';
    }
  }

  function hideError() {
    if (elements.errorMessage) {
      elements.errorMessage.style.display = 'none';
    }
  }

  // ===== EVENT HANDLERS =====
  function handleRefresh() {
    if (userLocation) {
      fetchWeatherData(userLocation.lat, userLocation.lon);
    } else {
      getUserLocation();
    }
  }

  // ===== INICIAR CUANDO EL DOM ESTÉ LISTO =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
